import React from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import type { TrainingProgram } from '../../types/trainingHost';

export const programStatusLabel: Record<TrainingProgram['status'], string> = {
  completed: '완료',
  upcoming: '예정',
  active: '진행 중',
};

export const programIsOpenable = (program: TrainingProgram) =>
  !!(program.programSlug && (program.contentType === 'editor' || program.contentHtml)) || !!program.path;

export type LobbyProfileDraft = {
  displayName: string;
  schoolName: string;
  bio: string;
  publicSlug: string;
};

type TrainingHostLobbyContentProps = {
  mode: 'view' | 'edit';
  photoURL?: string;
  profile: LobbyProfileDraft;
  programs: TrainingProgram[];
  showPrivatePrograms?: boolean;
  publicPath?: string;
  onProfileChange?: (field: keyof LobbyProfileDraft, value: string) => void;
  onOpenProgram?: (program: TrainingProgram) => void;
  onEditProgram?: (program: TrainingProgram) => void;
  onPreviewProgram?: (program: TrainingProgram) => void;
  addProgramSlot?: React.ReactNode;
  headerActions?: React.ReactNode;
};

const TrainingHostLobbyContent: React.FC<TrainingHostLobbyContentProps> = ({
  mode,
  photoURL,
  profile,
  programs,
  showPrivatePrograms = false,
  publicPath,
  onProfileChange,
  onOpenProgram,
  onEditProgram,
  onPreviewProgram,
  addProgramSlot,
  headerActions,
}) => {
  const isEdit = mode === 'edit';
  const visiblePrograms = showPrivatePrograms ? programs : programs.filter((p) => p.isPublic);

  const profileInputStyle = (fontSize: string, fontWeight: number): React.CSSProperties => ({
    width: '100%',
    border: isEdit ? '1px dashed var(--Color-line-normal)' : 'none',
    borderRadius: isEdit ? '8px' : 0,
    padding: isEdit ? '6px 10px' : 0,
    margin: 0,
    fontFamily: 'var(--font-family-primary)',
    fontSize,
    fontWeight,
    lineHeight: 1.5,
    color: 'var(--Color-text-strong)',
    background: isEdit ? 'var(--Color-background-normal)' : 'transparent',
    boxSizing: 'border-box',
  });

  const bioInputStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '560px',
    minHeight: '72px',
    border: isEdit ? '1px dashed var(--Color-line-normal)' : 'none',
    borderRadius: isEdit ? '8px' : 0,
    padding: isEdit ? '8px 10px' : 0,
    margin: 0,
    resize: 'vertical',
    fontFamily: 'var(--font-family-primary)',
    fontSize: '15px',
    lineHeight: 1.6,
    color: 'var(--Color-text-normal)',
    background: isEdit ? 'var(--Color-background-normal)' : 'transparent',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flex: 1, minWidth: '280px' }}>
          {photoURL ? (
            <img
              src={photoURL}
              alt=""
              style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: 'var(--Color-fill-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                flexShrink: 0,
              }}
            >
              🎓
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isEdit ? (
              <input
                value={profile.displayName}
                onChange={(e) => onProfileChange?.('displayName', e.target.value)}
                placeholder="이름"
                style={profileInputStyle('28px', 700)}
              />
            ) : (
              <h1 style={{ margin: '0 0 4px', fontSize: '28px', fontWeight: 700, color: 'var(--Color-text-strong)' }}>
                {profile.displayName || '이름 없음'}
              </h1>
            )}

            {isEdit ? (
              <input
                value={profile.schoolName}
                onChange={(e) => onProfileChange?.('schoolName', e.target.value)}
                placeholder="학교·기관 (선택)"
                style={{ ...profileInputStyle('15px', 400), marginTop: '8px', color: 'var(--Color-text-normal)' }}
              />
            ) : (
              profile.schoolName && (
                <p style={{ margin: '0 0 8px', fontSize: '15px', color: 'var(--Color-text-normal)' }}>{profile.schoolName}</p>
              )
            )}

            {isEdit ? (
              <textarea
                value={profile.bio}
                onChange={(e) => onProfileChange?.('bio', e.target.value)}
                placeholder="소개를 적어 주세요."
                style={{ ...bioInputStyle, marginTop: '8px' }}
              />
            ) : (
              profile.bio && (
                <p style={{ margin: 0, fontSize: '15px', color: 'var(--Color-text-normal)', lineHeight: 1.6, maxWidth: '560px' }}>
                  {profile.bio}
                </p>
              )
            )}

            {publicPath && (
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--Color-text-normal)', opacity: 0.8 }}>
                {publicPath}
              </p>
            )}
          </div>
        </div>
        {headerActions && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>{headerActions}</div>
        )}
      </div>

      <div style={{ width: '100%' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          연수 프로그램
        </h2>

        {visiblePrograms.length === 0 && !addProgramSlot ? (
          <Card padding="20px" borderRadius="16px" background="var(--Color-background-normal)" width="100%" style={{ boxSizing: 'border-box' }}>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
              {isEdit ? '아래에서 첫 연수를 추가해 보세요.' : '등록된 공개 연수가 없습니다.'}
            </p>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
            {visiblePrograms.map((program) => {
              const openable = programIsOpenable(program);
              const handleCardClick = () => {
                if (isEdit) return;
                if (openable) onOpenProgram?.(program);
              };

              return (
                <Card
                  key={program.id}
                  padding="16px 20px"
                  borderRadius="12px"
                  background={isEdit ? 'var(--Color-background-main)' : 'var(--Color-background-normal)'}
                  width="100%"
                  style={{
                    cursor: !isEdit && openable ? 'pointer' : 'default',
                    opacity: program.isPublic || showPrivatePrograms ? 1 : 0.6,
                    border: isEdit ? '1px solid var(--Color-line-normal)' : undefined,
                    boxSizing: 'border-box',
                  }}
                  onClick={handleCardClick}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                          alignItems: 'center',
                          marginBottom: '4px',
                        }}
                      >
                        <span style={{ fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
                          {program.title}
                        </span>
                        <span
                          style={{
                            fontSize: '12px',
                            padding: '2px 8px',
                            borderRadius: '999px',
                            backgroundColor: 'var(--Color-fill-hover)',
                            color: 'var(--Color-text-normal)',
                          }}
                        >
                          {programStatusLabel[program.status]}
                        </span>
                        {!program.isPublic && showPrivatePrograms && (
                          <span style={{ fontSize: '12px', color: 'var(--Color-text-normal)' }}>비공개</span>
                        )}
                      </div>
                      {program.description && (
                        <p style={{ margin: '0 0 4px', fontSize: '14px', color: 'var(--Color-text-normal)', lineHeight: 1.5 }}>
                          {program.description}
                        </p>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                        {program.date && <span>{program.date}</span>}
                        {program.category && <span>{program.category}</span>}
                      </div>
                    </div>
                    {isEdit ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {(program.contentType === 'editor' || program.contentHtml || program.contentType === 'legacy') && (
                          <Button variant="primary" size="medium" onClick={() => onEditProgram?.(program)}>
                            편집
                          </Button>
                        )}
                        {program.programSlug && (program.contentType === 'editor' || program.contentHtml) && (
                          <Button variant="outline" size="medium" onClick={() => onPreviewProgram?.(program)}>
                            미리보기
                          </Button>
                        )}
                        {program.contentType === 'legacy' && program.path && (
                          <Button variant="outline" size="medium" onClick={() => onOpenProgram?.(program)}>
                            링크 열기
                          </Button>
                        )}
                      </div>
                    ) : (
                      openable && (
                        <span style={{ fontSize: '13px', color: 'var(--Color-fill-brand-color)' }}>열기 →</span>
                      )
                    )}
                  </div>
                </Card>
              );
            })}
            {addProgramSlot}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingHostLobbyContent;
